import {
  createAsyncThunk, createSlice, PayloadAction,
} from '@reduxjs/toolkit';
import axios from 'axios';
import { off } from 'process';
import steps from './data/steps';
import Utils from '../../common/helpers/Utils';
import { Field, QuoteState, SelectOption } from '../../types';

const initialState: QuoteState = Utils.loadKCIQuoteData({
  data: {},
  ptc: window.kbSchema.transaction.ptc,
  fields: window.kbSchema.fields,
  fieldOptions: {},
  further: window.kbSchema.further,
  message: window.kbSchema.msg,
  showData: {},
  displayQueue: [],
  preUpdateQueue: [],
  updateQueue: [],
  activeShowFields: [],
  notes: {},
  removeQueue: {},
  paymentIframeHtml: null,
  triggeredFields: {},
  isLoading: false,
  isFetching: false,
  isRemoving: false,
  isUpdating: false,
  renderClones: 0,
  isDrawing: false,
  formQueue: [],
  removingQueue: [],
  loadingText: '',
  steps,
  inceptionDate: '',
  numberOfEmployees: 0,
  numberOfFeeEarners: 0,
  numberOfNonAdmin: 0,
  numberOfAdmin: 0,
  numberOfOffShore: 0,
  totalNumberOfStaff: 0,
  selectLoading: {},
  showFurtherQuestions: {},
  showAddOns: true,
  defaultValues: window.defaultValues,
  step: {
    step: window.kbSchema.step[0],
    innerStep: window.kbSchema.step[1],
  },
  quote: window.kbSchema.quote ? {
    json: window.kbSchema.quote,
  } : {},
  cyberRemoveQueue: {},
});

export const calculateQuote = createAsyncThunk(
  'quote/calculateQuote',
  async (data: any, { rejectWithValue }) => {
    const formData = new URLSearchParams();
    const defaultValues = JSON.parse(JSON.stringify(window.defaultValues));
    formData.append('ptc', window.kbSchema.transaction.ptc);
    formData.append('process_calculation', 'true');
    formData.append('resend_renewal_only', 'true');
    formData.append('ajax', '1');

    Object.keys(data).forEach((key: string) => {
      if (data[key]) {
        if (window.quickInfoData
          && key === window.quickInfoData.ernNumberCtrl) {
          Object.keys(data[key]).forEach((ernKey: string) => {
            formData.append(ernKey, data[key][ernKey]);
          });
          formData.append(key, Object.values(data[key]).join('/'));
        } else {
          formData.append(key, data[key]);
        }
        if (window.quickInfoData && key === window.quickInfoData.numberOfFeeEarnersCtrl) {
          formData.append('quote_sme', data[key] > 1 ? '1' : '0');
        }
      }
      if (Object.prototype.hasOwnProperty.call(defaultValues, key)) {
        delete defaultValues[key];
      }
    });
    Object.keys(defaultValues).forEach((key: string) => {
      if (defaultValues[key] !== null) formData.append(key, defaultValues[key]);
    });

    const response = await axios.post('/buy/get_quote.html', formData);

    if (response.status === 200 && response.data.type === 'success') {
      const priceResponse = await axios.get(`/quote_buy/ajax/price_update.html?json=1&${formData.toString()}`);
      if (priceResponse.status === 200 && priceResponse.data.json) {
        return priceResponse.data;
      }

      return rejectWithValue(priceResponse.data);
    }
    if (response.request.responseURL.indexOf('get_quote') === -1) {
      window.location.href = response.request.responseURL;
      return false;
    }
    return rejectWithValue(response.data);
  },
);

export const processPayment = createAsyncThunk(
  'quote/processPayment',
  async (data: any, { rejectWithValue }) => {
    const formData = new URLSearchParams();
    formData.append('json', '1');
    data.quote.forEach((quoteData: any) => {
      if (quoteData.formId === 'payment_cccancel' || quoteData.formId === 'payment_ccsubmit') return;
      if (quoteData.formId === 'payment_cardusebill') {
        if (quoteData.value === '1') {
          formData.append('payment_cardusebill', 'on');
        }
      } else if (quoteData.formId === 'payment_confirm') {
        if (quoteData.value === '1') {
          formData.append('payment_confirm', 'on');
        }
      } else if (quoteData.value) {
        formData.append(quoteData.formId, quoteData.value);
      }
    });

    const response = await axios.post(`/buy/payment.html?ajax=1&ptc=${data.ptc}`, formData);

    if ((response.status === 200 && (typeof response.data.msg !== 'string' && response.data.msg))
    || (response.status === 200 && typeof response.data === 'string')) {
      return response.data;
    }

    return rejectWithValue(response.data);
  },
);

export const priceUpdate = createAsyncThunk(
  'quote/priceUpdate',
  async (data: any, { rejectWithValue }) => {
    const formData = new URLSearchParams();
    formData.append('ptc', window.kbSchema.transaction.ptc);
    formData.append('email_quote', '1');
    formData.append('quote_submit', 'Continue >');
    formData.append('quote_sme', '0');

    Object.keys(data).forEach((key: string) => {
      if (window.quickInfoData && key === window.quickInfoData.numberOfFeeEarnersCtrl) {
        formData.append('quote_sme', data[key] > 1 ? '1' : '0');
      }
      formData.append(key, data[key]);
    });

    const response = await axios.get(`/quote_buy/ajax/price_update.html?${formData.toString()}`);
    if (response.status === 200 && response.data.json && !response.data.json.message) {
      return response.data;
    }

    return rejectWithValue(response.data);
  },
);

export const quoteSlice = createSlice({
  name: 'quote',
  initialState,
  reducers: {
    updateQuote: (state: QuoteState, data: any) => {
      data.payload.quote.forEach((field: any) => {
        if (field && field.formId) {
          state.data[field.formId] = field.value;
          quoteSlice.caseReducers.updateField(state, {
            payload: {
              formId: field.formId,
              attribute: 'value',
              value: field.value,
            },
          });
        }
      });
    },
    removeFromData: (state: QuoteState, data: any) => {
      data.payload.forEach((formId: string) => {
        delete state.data[formId];
      });
    },
    updateField: (state: QuoteState, data: any) => {
      state.steps.forEach((outerStep: any, stepIndex: number) => {
        outerStep.steps.forEach((innerStep: any, innerStepIndex: number) => {
          innerStep.fields.forEach((field: Field, index: number) => {
            if (field.formId === data.payload.formId) {
              state.steps[stepIndex].steps[innerStepIndex]
                .fields[index][data.payload.attribute] = data.payload.value;
            }
          });
        });
      });
    },
    setTotalNumberOfStaff(state: QuoteState, data: any) {
      state.totalNumberOfStaff = data.payload;
    },
    goToNextStep: (state: QuoteState) => {
      const stepObject = state.steps[state.step.step];
      const nextInnerStep = state.step.innerStep + 1;
      const stateToPush = {
        kb_quote: {
          data: state.data,
          step: state.step,
          savedAt: Date.now(),
        },
      };
      if (stepObject.steps.length === nextInnerStep) {
        const nextStep = state.step.step + 1;
        if (nextStep === state.steps.length) {
          //
        } else {
          // handle user pressing browser back button instead of page button
          window.history.replaceState(JSON.stringify(stateToPush), '');
          window.history.pushState(JSON.stringify(stateToPush), '');
          state.step = {
            innerStep: 0,
            step: nextStep,
          };
          state.isLoading = false;
        }
      } else {
        window.history.replaceState(JSON.stringify(stateToPush), '');
        window.history.pushState(JSON.stringify(stateToPush), '');
        state.step = {
          innerStep: nextInnerStep,
          step: state.step.step,
        };
        state.isLoading = false;
      }
    },
    goToPrevStep: (state: QuoteState) => {
      const nextInnerStep = state.step.innerStep - 1;
      if (nextInnerStep < 0) {
        const nextStep = Math.max(state.step.step - 1, 0);
        state.step = {
          innerStep: state.steps[nextStep].steps.length - 1,
          step: nextStep,
        };
      } else {
        state.step = {
          innerStep: nextInnerStep,
          step: state.step.step,
        };
      }
    },
    goToStep: (state: QuoteState, data: any) => {
      state.step = {
        innerStep: data.payload.innerStep,
        step: data.payload.step,
      };
    },
    setIsLoading: (state: QuoteState, data: any) => {
      state.isLoading = data.payload;
    },
    setIsFetching: (state: QuoteState, data: any) => {
      state.isFetching = data.payload;
    },
    setIsUpdating: (state: QuoteState, data: any) => {
      state.isUpdating = data.payload;
    },
    setIsDrawing: (state: QuoteState, data: any) => {
      state.isDrawing = data.payload;
    },
    setIsRemoving: (state: QuoteState, data: any) => {
      state.isRemoving = data.payload;
    },
    setLoadingText: (state: QuoteState, data: any) => {
      state.loadingText = data.payload;
    },
    updateNumberOfEmployees: (state: QuoteState, data: any) => {
      state.numberOfEmployees = data.payload.value;
    },
    updateNumberOfFeeEarners: (state: QuoteState, data: any) => {
      state.numberOfFeeEarners = data.payload.value;
      state.renderClones = data.payload.value;
    },
    updateNumberOfOffShore: (state: QuoteState, data: any) => {
      state.numberOfOffShore = data.payload.value;
    },
    setNumberOfRenderClones: (state: QuoteState, data: any) => {
      state.renderClones = data.payload.value;
    },
    updateNumberOfAdminStaff: (state: QuoteState, data: any) => {
      state.numberOfAdmin = data.payload.value;
    },
    updateNumberOfNonAdminStaff: (state: QuoteState, data: any) => {
      state.numberOfNonAdmin = data.payload.value;
    },
    updateInceptionDate: (state: QuoteState, data: any) => {
      state.inceptionDate = data.payload;
    },
    setMessage: (state: QuoteState, data: any) => {
      state.message = data.payload;
    },
    addToUpdateQueue: (state: QuoteState, data: any) => {
      state.updateQueue = state.updateQueue.concat(data.payload);
    },
    addToPreUpdateQueue: (state: QuoteState, data: any) => {
      state.preUpdateQueue = state.preUpdateQueue.concat(data.payload);
    },
    clearUpdateQueue: (state: QuoteState) => {
      state.updateQueue = [];
    },
    clearPreUpdateQueue: (state: QuoteState) => {
      state.preUpdateQueue = [];
    },
    setSelectLoading: (state: QuoteState, data: any) => {
      state.selectLoading[data.payload.key] = data.payload.loading;
    },
    updateOptionsForControl: (state: QuoteState, data: any) => {
      state.fieldOptions[data.payload.formId] = data.payload.options;
    },
    removeOptionFromControl: (state: QuoteState, data: any) => {
      data.payload.options.forEach((option: SelectOption) => {
        const removeIndex = (state.fieldOptions[data.payload.formId]).findIndex(
          (currentOption: any) => currentOption.value === option.value,
        );
        if (removeIndex > -1) {
          state.fieldOptions[data.payload.formId].splice(removeIndex, 1);
        }
      });
    },
    addOptionForControl: (state: QuoteState, data: any) => {
      if (data.payload.options) {
        data.payload.options.forEach((option: SelectOption) => {
          if (state.fieldOptions[data.payload.formId].findIndex(
            (currentOption: any) => currentOption.value === option.value,
          ) === -1) {
            state.fieldOptions[data.payload.formId].push(option);
          }
        });
      }
    },
    addToDisplayQueue: (state: QuoteState, data: any) => {
      // Only add to the display queue if not already on it
      state.displayQueue = state.displayQueue.concat(data.payload);
    },
    removeFromDisplayQueue: (state: QuoteState, data: PayloadAction<any>) => {
      // Wer're gooing to find item and remove it
      const index = state.displayQueue.findIndex(
        (dq: any) => dq.fields.findIndex(
          (field: Field) => field.formId === data.payload,
        ) > -1,
      );

      if (index > -1) {
        state.displayQueue.splice(index, 1);
      }
    },
    removeFromRemoveQueue: (state: QuoteState, data: PayloadAction<any>) => {
      // Wer're gooing to find item and remove it
      Object.keys(state.removeQueue).forEach((trigger: string) => {
        const index = state.removeQueue[trigger].findIndex(
          (formId: string) => formId === data.payload,
        );
        if (index > -1) {
          state.removeQueue[trigger].splice(index, 1);
        }
      });
    },
    addToRemoving: (state: QuoteState, data: any) => {
      state.removingQueue = data.payload;
    },
    addToForm: (state: QuoteState, data: any) => {
      state.formQueue = state.formQueue.concat(data.payload);
      state.isLoading = true;
    },
    addToRemoveQueue: (state: QuoteState, data: any) => {
      state.removeQueue[data.payload.trigger] = data.payload.fields;
    },
    clearRemove: (state: QuoteState) => {
      state.removeQueue = {};
    },
    addToShowData: (state: QuoteState, action: PayloadAction<any>) => {
      state.showData[action.payload.field.formId] = {
        field: action.payload.field,
        fieldIndex: action.payload.fieldIndex,
        step: action.payload.step,
        innerStep: action.payload.innerStep,
      };
    },
    clearRemoving: (state: QuoteState) => {
      state.removingQueue = {};
    },
    clearQueue: (state: QuoteState) => {
      state.displayQueue = [];
    },
    clearFormQueue: (state: QuoteState) => {
      state.formQueue = [];
      state.isDrawing = false;
    },
    addTriggerToRemoveQueue(state: QuoteState, data: any) {
      state.cyberRemoveQueue[data.payload.prop] = data.payload.trigger;
    },
    removeTriggerFromRemoveQueue: (state: QuoteState, data: PayloadAction<any>) => {
      if (Object.prototype.hasOwnProperty.call(state.cyberRemoveQueue, data.payload)) {
        delete state.cyberRemoveQueue[data.payload];
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(calculateQuote.fulfilled, (state, action: PayloadAction<any>) => {
      if (action.payload) {
        state.isLoading = false;
        state.quote = action.payload;
        quoteSlice.caseReducers.goToNextStep(state);
      }
    });
    builder.addCase(calculateQuote.rejected, (state, action: any) => {
      state.isLoading = false;
      state.message = action.payload.msg || action.payload.json?.message || 'An internal error has occurred, please contact our support team on 01242 808740 for further assistance.';

      quoteSlice.caseReducers.goToStep(state, {
        payload: {
          innerStep: 0,
          step: 0,
        },
      });
    });
    builder.addCase(priceUpdate.fulfilled, (state, action) => {
      state.quote = action.payload;
    });
    builder.addCase(priceUpdate.rejected, (state, action: any) => {
      // state.quote = action.payload;action.payload.message

      // Go to first step and print message
    });
    builder.addCase(processPayment.rejected, (state, action: any) => {
      state.isLoading = false;
      state.message = action.payload.msg || action.payload.message;
    });
    builder.addCase(processPayment.fulfilled, (state, action: any) => {
      state.isLoading = false;
      state.paymentIframeHtml = action.payload;
      state.message = action.payload.msg || null;
    });
  },
});

export const getQuote = (state: any) => state.quote;
export const getQuoteData = (state: any) => state.quote.data;
export const getCurrentStep = (state: any) => {
  if (state.quote.step) {
    return state.quote.steps[state.quote.step.step]
      .steps[state.quote.step.innerStep];
  }
  return null;
};
export const getCurrentStepFields = (state: any) => state.steps[state.step.step]
  .steps[state.step.innerStep].fields;
export const getCyberRemoveQueue = (state: any) => state.quote.cyberRemoveQueue;

export const { actions } = quoteSlice;

export default quoteSlice.reducer;
